import React from 'react';
import {
  Form,
  Row,
  Col,
} from 'antd';

import {
  useQuery,
  useMutation,
} from '@apollo/client';
import UploadCard from 'components/cards/UploadCard';

import BUSINESS from '../../graphql/business.gql';
import SAVE_BUSINESS from '../../graphql/saveBusiness.gql';
import ADD_ADDITIONAL_FILE from '../../graphql/addAdditionalFile.gql';
import getBadesulBusinessDocuments from './badesulDocuments';

const { Item } = Form;

const BusinessDocuments = ({
  businessId,
  ownerValue,
  portfolioName,
  loading: parentLoading,
}) => {
  const isBadesul = ownerValue === 'badesul';

  const {
    data: {
      business,
    } = {},
    refetch,
  } = useQuery(BUSINESS, {
    variables: {
      id: businessId,
    },
    skip: !businessId,
    fetchPolicy: 'cache-only',
  });

  const [saveBusiness] = useMutation(SAVE_BUSINESS, {
    onCompleted: () => refetch(),
  });

  const [addAdditionalFile] = useMutation(ADD_ADDITIONAL_FILE, {
    onCompleted: () => refetch(),
  });

  // Último arquivo enviado de cada tipo, indexado por parent.name.
  const additionalFileByParent = (business?.additionalFiles ?? []).reduce((acc, file) => {
    const parentName = file.parent?.name;
    if (parentName) {
      acc[parentName] = file;
    }
    return acc;
  }, {});

  // Documento contratual e comprovante de endereço são sempre pedidos e
  // persistidos via saveBusiness (mesmo comportamento do app).
  const baseDocuments = [
    {
      value: 'bylawsFileId',
      file: business?.bylaws,
      name: 'Documento contratual da empresa',
      save: (fileId) => saveBusiness({ variables: { id: business.id, bylawsFileId: fileId } }),
    },
    {
      value: 'proofOfAddressFileId',
      file: business?.address?.proofOfAddress,
      name: 'Comprovante de endereço',
      save: (fileId) => saveBusiness({
        variables: { id: business.id, proofOfAddressFileId: fileId },
      }),
    },
  ];

  let files;
  if (isBadesul) {
    // Espelha o app: cada produto Badesul pede uma lista específica de documentos
    // adicionais, lidos/salvos como additionalFiles (parent.name === documentValue).
    // Muitos vêm pré-preenchidos quando a integração já os trouxe.
    const badesulDocuments = getBadesulBusinessDocuments(portfolioName).map(({
      documentValue,
      label,
      alertMessage,
    }) => ({
      value: documentValue,
      file: additionalFileByParent[documentValue],
      name: label,
      alert: alertMessage,
      save: (fileId) => addAdditionalFile({
        variables: {
          id: fileId,
          businessId: business.id,
          name: documentValue,
          multiple: false,
        },
      }),
    }));

    files = [...baseDocuments, ...badesulDocuments];
  } else {
    // Fluxo Badesc/Pronampe (inalterado).
    files = [
      ...baseDocuments,
      {
        value: 'badescRaisPersonFileId',
        file: additionalFileByParent.raisPerson,
        name: 'RAIS e-social',
        save: (fileId) => saveBusiness({
          variables: { id: business.id, badescRaisPersonFileId: fileId },
        }),
      },
      {
        value: 'badescCndScEnvironmentalCrimesFileId',
        file: additionalFileByParent.cndScEnvironmentalCrimes,
        name: 'CND - Crimes Ambientais SC',
        save: (fileId) => saveBusiness({
          variables: { id: business.id, badescCndScEnvironmentalCrimesFileId: fileId },
        }),
      },
    ];
  }

  const loading = parentLoading || !business;

  return (
    <Form>
      <Row gutter={12}>
        {files.map(({
          value, name, file, alert, save,
        }) => (
          <Col xs={24} sm={24} key={value}>
            <Item
              name={value}
              rules={[{ required: true, message: `Por favor, insira o ${name}` }]}
            >
              <UploadCard
                id={`${value}-upload`}
                accept="image/png,image/jpeg,application/pdf"
                loading={loading}
                label={name}
                alert={alert}
                actualFile={file}
                onCompleted={async ({ id: fileId }) => {
                  await save(fileId);
                }}
              />
            </Item>
          </Col>
        ))}

      </Row>
    </Form>
  );
};

export default BusinessDocuments;
